import React, { Suspense, lazy, useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { API_BASE_URL } from './config/api';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { FeatureFlagProvider, useFeatureFlag } from './contexts/FeatureFlagContext';
import { Toaster } from './design';

// --- Eager: customer-critical above-the-fold routes ---
import CustomerHome from './pages/CustomerHome';
import Catalog from './pages/Catalog';
import ProductsNew from './pages/ProductsNew';
import Cart from './pages/Cart';
import RestaurantCheckoutPage from './pages/RestaurantCheckout';
import CheckoutPage from './pages/Checkout';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';

// --- Lazy: secondary / role-gated routes (split into separate chunks) ---
const StoreList = lazy(() => import('./pages/StoreList'));
const Restaurants = lazy(() => import('./pages/Restaurants'));
const RestaurantMenu = lazy(() => import('./pages/RestaurantMenu'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmation'));
const OrderDetailsPage = lazy(() => import('./pages/OrderDetails'));
const OrderTracking = lazy(() => import('./pages/OrderTracking'));
const LiveTracking = lazy(() => import('./pages/LiveTracking'));
const PaymentPage = lazy(() => import('./pages/Payment'));
const NotificationsCenter = lazy(() => import('./pages/Notifications'));
const ReviewsPage = lazy(() => import('./pages/Reviews'));
const SignupDualOTP = lazy(() => import('./pages/auth/SignupDualOTP'));
const StoreSignup = lazy(() => import('./pages/auth/StoreSignup'));
const DeliverySignup = lazy(() => import('./pages/auth/DeliverySignup'));
const Profile = lazy(() => import('./pages/Profile'));
const StoreDashboard = lazy(() => import('./pages/store/Dashboard'));
const DeliveryList = lazy(() => import('./pages/delivery/DeliveryList'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBulkImport = lazy(() => import('./pages/admin/AdminBulkImport'));
const RideBook = lazy(() => import('./pages/rides/RideBook'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));

/** Lightweight inline spinner used as the Suspense fallback for lazy routes. */
const RouteFallback: React.FC = () => (
  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
    <div className="spinner-border text-success" role="status" style={{ width: '2rem', height: '2rem' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check API health on app load
    fetch(`${API_BASE_URL}/healthz`, { signal: AbortSignal.timeout(5000) })
      .then((response) => {
        if (response.ok) console.log('API is healthy');
      })
      .catch((error) => {
        console.error('API health check failed:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <FeatureFlagProvider>
      <Router>
        <ErrorBoundary scope="App">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup/strict" element={<SignupDualOTP />} />
        <Route path="/register/store" element={<StoreSignup />} />
        <Route path="/register/delivery" element={<DeliverySignup />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Customer Routes */}
        <Route
          path="/"
          element={
            <Layout>
              <CustomerHome />
            </Layout>
          }
        />
        <Route
          path="/stores"
          element={
            <FeatureRoute flag="stores">
              <Layout>
                <StoreList />
              </Layout>
            </FeatureRoute>
          }
        />
        <Route
          path="/catalog"
          element={
            <FeatureRoute flag="catalog">
              <Layout>
                <Catalog />
              </Layout>
            </FeatureRoute>
          }
        />
        <Route
          path="/store/:storeId"
          element={
            <FeatureRoute flag="stores">
              <Layout>
                <ProductsNew />
              </Layout>
            </FeatureRoute>
          }
        />
        <Route
          path="/restaurants"
          element={
            <FeatureRoute flag="food">
              <Layout>
                <Restaurants />
              </Layout>
            </FeatureRoute>
          }
        />
        <Route
          path="/restaurant/:restaurantId"
          element={
            <FeatureRoute flag="food">
              <Layout>
                <RestaurantMenu />
              </Layout>
            </FeatureRoute>
          }
        />
        <Route
          path="/product/:productId"
          element={
            <Layout>
              <ProductDetail />
            </Layout>
          }
        />
        <Route
          path="/cart"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <Cart />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <CheckoutPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurant-checkout"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <RestaurantCheckoutPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/order-confirmed/:orderId"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <OrderConfirmationPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/order/:orderId"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <OrderDetailsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <OrderTracking />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/order/:orderId/track"
          element={
            <ProtectedRoute>
              <Layout>
                <LiveTracking />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment/:orderId"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <PaymentPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <NotificationsCenter />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/order/:orderId/reviews"
          element={
            <ProtectedRoute requiredRoles={['customer']}>
              <Layout>
                <ReviewsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Store Routes — wrapped in their own ErrorBoundary so a crash here
            doesn't kill the customer shell. */}
        <Route
          path="/store-dashboard"
          element={
            <ProtectedRoute requiredRoles={['store']}>
              <Layout>
                <ErrorBoundary scope="Store Dashboard">
                  <StoreDashboard />
                </ErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Delivery Agent Routes */}
        <Route
          path="/deliveries"
          element={
            <ProtectedRoute requiredRoles={['delivery_agent']}>
              <Layout>
                <ErrorBoundary scope="Deliveries">
                  <DeliveryList />
                </ErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes — isolated boundary */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <Layout>
                <ErrorBoundary scope="Admin">
                  <AdminDashboard />
                </ErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bulk-import"
          element={
            <ProtectedRoute requiredRoles={['admin', 'store']}>
              <Layout>
                <ErrorBoundary scope="Bulk Import">
                  <AdminBulkImport />
                </ErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Ride Booking Routes */}
        <Route
          path="/rides/:rideType"
          element={
            <FeatureRoute flag="rides">
              <Layout>
                <RideBook />
              </Layout>
            </FeatureRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>

      {/* Branded toast notifications (Phase 1 design system). */}
      <Toaster />
      </Router>
    </FeatureFlagProvider>
  );
}

/**
 * FeatureRoute — renders children only if the feature flag is enabled.
 * Disabled features silently redirect to home (no 404, per product spec).
 */
const FeatureRoute: React.FC<{ flag: string; children: React.ReactNode }> = ({ flag, children }) => {
  const enabled = useFeatureFlag(flag);
  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
};

export default App;
